import log from '@/lib/logger';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardDocumentCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Text } from '@/components/tailwind/text';
import { useAuth } from '@/context/use-auth';
import { getDashboardPendingConfirmations, DashboardPendingConfirmations } from '@/services/dashboard-services';

export function PendingConfirmationsWidget() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [confirmations, setConfirmations] = useState<DashboardPendingConfirmations | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function loadConfirmations() {
            if (!token) return;

            setIsLoading(true);
            setError(null);
            try {
                const data = await getDashboardPendingConfirmations(token);
                setConfirmations(data);
            } catch (err) {
                log.error('Failed to load pending confirmations:', err);
                setError(t('dashboard.widgets.pendingConfirmations.error'));
            } finally {
                setIsLoading(false);
            }
        }

        loadConfirmations();
    }, [token, t]);

    function handleNdaClick() {
        navigate('/members?missingConfidentiality=true');
    }

    function handlePrivacyClick() {
        navigate('/members?missingPrivacyConsent=true');
    }

    function handleMediaClick() {
        navigate('/members?missingMediaConsent=true');
    }

    if (isLoading) {
        return (
            <div 
                className="rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-6" 
                data-testid="pending-confirmations-widget"
            >
                <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                    <div className="space-y-3">
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div 
                className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-6" 
                data-testid="pending-confirmations-widget"
            >
                <div className="text-red-800 dark:text-red-200 text-sm">{error}</div>
            </div>
        );
    }

    const hasPending = (confirmations?.totalPendingCount ?? 0) > 0;
    const borderClass = hasPending 
        ? 'border-amber-300 dark:border-amber-600' 
        : 'border-gray-200 dark:border-gray-800';
    const bgClass = hasPending 
        ? 'bg-amber-50 dark:bg-amber-950/30' 
        : 'bg-white dark:bg-gray-900';

    return (
        <div
            className={`rounded-lg shadow-sm border ${borderClass} ${bgClass} p-6`}
            data-testid="pending-confirmations-widget"
        >
            <div className="flex items-center space-x-2 mb-4">
                {hasPending ? (
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                ) : (
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('dashboard.widgets.pendingConfirmations.title')}
                </h3>
            </div>

            {!hasPending ? (
                <div className="flex items-center space-x-2">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                        {t('dashboard.widgets.pendingConfirmations.allComplete')}
                    </span>
                </div>
            ) : (
                <div className="space-y-3">
                    {/* NDA */}
                    {(confirmations?.pendingNdaCount ?? 0) > 0 && (
                        <button
                            onClick={handleNdaClick}
                            className="w-full flex items-center justify-between p-2 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left"
                            data-testid="pending-nda-link"
                        >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {t('dashboard.widgets.pendingConfirmations.nda')}
                            </span>
                            <span className="font-semibold text-amber-700 dark:text-amber-400" data-testid="pending-nda-count">
                                {confirmations?.pendingNdaCount}
                            </span>
                        </button>
                    )}

                    {/* Privacy Consent */}
                    {(confirmations?.pendingPrivacyConsentCount ?? 0) > 0 && (
                        <button
                            onClick={handlePrivacyClick}
                            className="w-full flex items-center justify-between p-2 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left"
                            data-testid="pending-privacy-link"
                        >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {t('dashboard.widgets.pendingConfirmations.privacy')}
                            </span>
                            <span className="font-semibold text-amber-700 dark:text-amber-400" data-testid="pending-privacy-count">
                                {confirmations?.pendingPrivacyConsentCount}
                            </span>
                        </button>
                    )}

                    {/* Media Consent */}
                    {(confirmations?.pendingMediaConsentCount ?? 0) > 0 && (
                        <button
                            onClick={handleMediaClick}
                            className="w-full flex items-center justify-between p-2 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left"
                            data-testid="pending-media-link"
                        >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {t('dashboard.widgets.pendingConfirmations.media')}
                            </span>
                            <span className="font-semibold text-amber-700 dark:text-amber-400" data-testid="pending-media-count">
                                {confirmations?.pendingMediaConsentCount}
                            </span>
                        </button>
                    )}

                    <Text className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-amber-200 dark:border-amber-700">
                        {t('dashboard.widgets.pendingConfirmations.clickToFilter')}
                    </Text>
                </div>
            )}
        </div>
    );
}
